/**
 * Color coding utilities for technician and stage-based job coloring.
 * Used in Today view (tech colors) and Calendar view (stage/tech toggle).
 */

// 8 curated muted wash/accent pairs
export const COLOR_PALETTE = [
  { name: 'Blue',    wash: '#EFF6FF', accent: '#3B82F6' },
  { name: 'Green',   wash: '#ECFDF5', accent: '#10B981' },
  { name: 'Amber',   wash: '#FFFBEB', accent: '#F59E0B' },
  { name: 'Red',     wash: '#FEF2F2', accent: '#EF4444' },
  { name: 'Purple',  wash: '#F5F3FF', accent: '#8B5CF6' },
  { name: 'Teal',    wash: '#F0FDFA', accent: '#14B8A6' },
  { name: 'Pink',    wash: '#FDF2F8', accent: '#EC4899' },
  { name: 'Indigo',  wash: '#EEF2FF', accent: '#6366F1' },
];

// Default stage colors — keyed by requests.current_state (job lifecycle)
export const DEFAULT_STAGE_COLORS = {
  scheduled: '#3B82F6',
  received:  '#8B5CF6',
  started:   '#F59E0B',
  finished:  '#10B981',
  collected: '#EC4899',
};

/**
 * Look up the wash color for a given accent hex.
 * Returns the wash from the palette, or a light fallback.
 */
export function getWashFromAccent(accent) {
  if (!accent) return null;
  const match = COLOR_PALETTE.find(c => c.accent.toLowerCase() === accent.toLowerCase());
  return match ? match.wash : accent + '18'; // fallback: accent with low opacity
}

/**
 * Get technician color from the staff/technicians list.
 * Matches by ID first, then by name as fallback (calendar data may only have name).
 * Returns { wash, accent } or null if no color is set.
 */
export function getTechnicianColor(techIdOrName, technicians) {
  if (!techIdOrName || !Array.isArray(technicians)) return null;
  // Try ID match first
  let tech = technicians.find(t =>
    String(t.User_id) === String(techIdOrName) || String(t.user_id) === String(techIdOrName)
  );
  // Fallback: name match (for calendar data that only has assignedTechnicianName)
  if (!tech && typeof techIdOrName === 'string' && isNaN(techIdOrName)) {
    tech = technicians.find(t =>
      (t.user_name || '').toLowerCase() === techIdOrName.toLowerCase()
    );
  }
  if (!tech || !tech.user_color) return null;
  const accent = tech.user_color;
  const wash = getWashFromAccent(accent);
  return { wash, accent };
}

/**
 * Get stage color for a workflow state.
 * Uses installer's custom stage colors if available, else defaults.
 * Returns { wash, accent } or null if no color for that state.
 */
export function getStageColor(state, installerStageColors) {
  const colors = installerStageColors || {};
  const accent = colors[state] || DEFAULT_STAGE_COLORS[state];
  if (!accent) return null;
  const wash = getWashFromAccent(accent);
  return { wash, accent };
}
